import { useMemo, useRef, useState } from 'react'
import Axios from '../redux/axiosInstance'
import { apiEndpoints } from '../api'
import { toast } from 'sonner'
import useLanguageTranslate from './useLanguageTranslate'

// Convert a Blob to a base64 string via FileReader.
// Used for the native WKWebView bridge path — FileReader is more reliable
// than blob.arrayBuffer() on older iOS WebKit builds.
function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            // result = "data:<mime>;base64,<data>" — strip the data-URL prefix
            resolve(reader.result.split(',')[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

// --- Native WKWebView bridge ------------------------------------------------
// When the app runs inside the iOS native wrapper, window.location.href on a
// blob URL does NOT trigger Apple Wallet — WKWebView doesn't intercept
// application/vnd.apple.pkpass on its own. We pass the file as base64 to the
// native app, which opens it with PKAddPassesViewController.
//
// Native team: register a WKScriptMessageHandler named "appleWallet":
//   payload = { base64: String, filename: String }
//   → Data(base64Encoded: base64)
//   → write to temp/<filename>
//   → PKAddPassesViewController(pass: PKPass(data:))
const tryNativeBridge = (base64, filename) => {
    try {
        const handler = window?.webkit?.messageHandlers?.appleWallet;
        if (handler && typeof handler.postMessage === 'function') {
            handler.postMessage({ base64, filename });
            return true;
        }
    } catch {
        // Not inside the iOS native wrapper — safe to ignore.
    }
    return false;
};

// Trigger a file-save via an anchor click.
// Rules that matter on iOS Safari:
//   • a.download must be set before clicking — without it the browser uses the
//     URL's last path segment ("generatePolicyPass") as the filename, stripping
//     the .pkpass extension.
//   • The <a> must be appended to the DOM before .click() — iOS Safari ignores
//     clicks on detached elements.
// This saves to Files / Downloads. The user then taps the file to open Wallet.
// (Auto Wallet hand-off only works when navigating to a real HTTPS URL that
// serves application/vnd.apple.pkpass — blob URLs don't qualify.)
const triggerAnchorDownload = (objectUrl, filename) => {
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
        a.remove();
        URL.revokeObjectURL(objectUrl);
    }, 1000);
};

const useAppleWalletApi = (policyNumber) => {
    const [isLoading, setIsLoading] = useState(false);
    const inFlightRef = useRef(false);
    const translate = useLanguageTranslate();

    const isAppleDevice = useMemo(() => {
        const userAgent = navigator.userAgent || '';
        const platform = navigator.platform || '';
        const isIOSUserAgent = /iPhone|iPad|iPod/i.test(userAgent);
        const isIPadOS = platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
        const isIOSPlatform = /iPhone|iPad|iPod/i.test(platform);
        return isIOSUserAgent || isIOSPlatform || isIPadOS;
    }, []);

    const AppleWalletApiTrigger = async () => {
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        setIsLoading(true);

        try {
            let pkpassBlob;

            try {
                const response = await Axios.post(
                    apiEndpoints.appleWalletGenerateFileId,
                    { policyNumber },
                    { responseType: 'blob' }
                );

                // Guard: 2xx but Content-Type is JSON means an error slipped through.
                const contentType = response.headers?.['content-type'] || '';
                if (contentType.includes('application/json')) {
                    const text = await response.data.text();
                    const err = JSON.parse(text);
                    toast.error(translate(
                        err.ErrorMessageEn || "Failed to generate Wallet pass",
                        err.ErrorMessageAr || "فشل في إنشاء بطاقة المحفظة"
                    ));
                    return;
                }

                pkpassBlob = response.data;
            } catch (e) {
                // Axios error (4xx/5xx). With responseType:'blob', e.response.data
                // is a Blob containing the JSON error body.
                if (e.response?.data instanceof Blob) {
                    try {
                        const text = await e.response.data.text();
                        const err = JSON.parse(text);
                        toast.error(translate(
                            err.ErrorMessageEn || "Failed to generate Wallet pass",
                            err.ErrorMessageAr || "فشل في إنشاء بطاقة المحفظة"
                        ));
                    } catch {
                        toast.error(translate(
                            "Failed to generate Wallet pass",
                            "فشل في إنشاء بطاقة المحفظة"
                        ));
                    }
                } else {
                    toast.error(translate(
                        "Failed to generate Wallet pass",
                        "فشل في إنشاء بطاقة المحفظة"
                    ));
                }
                return;
            }

            // Filename derived from the policy number — slashes → dashes.
            // We do NOT rely on Content-Disposition because it doesn't survive
            // blob URL materialisation; a.download is what the browser honours.
            const filename = `${(policyNumber || 'pass').replace(/\//g, '-')}.pkpass`;

            // Enforce correct MIME type so the OS knows this is a Wallet pass.
            const blob = new Blob([pkpassBlob], { type: 'application/vnd.apple.pkpass' });

            if (isAppleDevice) {
                // Strategy 1: native WKWebView bridge.
                // The native app receives the file as base64 and presents
                // PKAddPassesViewController — the only path to a direct
                // "Add to Apple Wallet" prompt without a Files-app detour.
                const base64 = await blobToBase64(blob);
                if (tryNativeBridge(base64, filename)) return;

                // Strategy 2: anchor download (no native wrapper present).
                // Saves to Files / Downloads. User taps the .pkpass file to open
                // Wallet. Direct Wallet hand-off from a blob URL is not possible —
                // iOS only auto-opens Wallet when navigating to a real HTTPS URL
                // serving application/vnd.apple.pkpass.
                const objectUrl = URL.createObjectURL(blob);
                triggerAnchorDownload(objectUrl, filename);
            } else {
                // Desktop / Android: anchor-click download.
                const objectUrl = URL.createObjectURL(blob);
                triggerAnchorDownload(objectUrl, filename);
            }
        } finally {
            setIsLoading(false);
            inFlightRef.current = false;
        }
    };

    return {
        isLoading,
        AppleWalletApiTrigger,
        isAppleDevice
    };
};

export default useAppleWalletApi;
